import { useState, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { aboutInfo } from '@/mocks/faq';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9n16c4it35ii3jdln80';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value.trim();

    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    const url = CONTACT_FORM_URL;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的网络连接');
      setFormStatus('error');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '糖心视频',
    url: 'https://www.tangxinsp.com/',
    description: '糖心视频网聚合西瓜视频、樱花视频、糖豆视频、樱桃视频等热门平台，最新电影、电视剧、综艺全收录。',
    email: aboutInfo.contact.email,
    telephone: aboutInfo.contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于糖心视频',
    description: '了解糖心视频的品牌故事、用户口碑和联系方式。',
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 糖心视频 | 品牌介绍、用户口碑与联系方式"
        description="了解糖心视频的品牌故事与发展历程，查看真实用户口碑评价，获取联系我们的一切方式。糖心视频致力于为影迷提供最优质的影视聚合服务。"
        keywords="关于糖心视频, 糖心视频品牌, 影视平台介绍, 联系我们"
        canonicalUrl="https://www.tangxinsp.com/about"
        jsonLd={orgJsonLd}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '关于我们' },
              ]}
            />
          </div>
        </div>

        <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-6">
              {aboutInfo.brandIntro.title}
            </h1>
            <div className="space-y-4 text-foreground-600 leading-relaxed text-sm md:text-base max-w-3xl mx-auto">
              {aboutInfo.brandIntro.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        <section id="reputation" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {aboutInfo.reputation.title}
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">来自真实用户的心声</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {aboutInfo.reputation.testimonials.map((t, i) => (
                <div
                  key={i}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="flex items-center gap-1 mt-3">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {aboutInfo.contact.title}
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                {aboutInfo.contact.description}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">电子邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">微信</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">地址</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">电话</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">发送消息</h3>
                <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="contact-form-hp" />
                  <div className="space-y-4">
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300"
                        required
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的消息..."
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 resize-none"
                        required
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg whitespace-nowrap transition-colors cursor-pointer text-sm"
                    >
                      发送消息
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-600 text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}